import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FC, MouseEventHandler } from 'react';
import { useBreakpointValue } from '@chakra-ui/react';
import { getAssetUrl } from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';
import isEqual from 'lodash-es/isEqual';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { useDoubleTap } from 'use-double-tap';
import type { ReactZoomPanPinchContentRef, ReactZoomPanPinchProps } from 'react-zoom-pan-pinch';
import { ResponsiveImage } from '../../../responsive-image';
import styles from './ImageAsset.module.scss';
import type { AssetInfo } from '../../utils';

export interface TouchCoords {
    x: number;
    y: number;
}

interface ImageAssetProps {
    asset: AssetInfo;
}

export const ImageAsset: FC<ImageAssetProps> = ({ asset }) => {
    const [startingCoords, setStartingCoords] = useState<TouchCoords | null>(null);
    const [scale, setScale] = useState(1);
    const pinchZoomRef = useRef<ReactZoomPanPinchContentRef>(null);

    useEffect(() => {
        pinchZoomRef.current?.resetTransform();
    }, [asset]);

    // #region handlers for single click/tap zoom
    const handleOnMouseDown: MouseEventHandler<HTMLDivElement> = useCallback((evt) => {
        setStartingCoords({ x: evt.clientX, y: evt.clientY });
    }, []);

    const handleOnClick: MouseEventHandler<HTMLDivElement> = useCallback(
        (evt) => {
            if (!isEqual(startingCoords, { x: evt.clientX, y: evt.clientY })) {
                setStartingCoords(null);
                return;
            }
            setStartingCoords(null);

            // If the user pinch & zoomed over 2 scale, this will reset the zoom level
            if (scale >= 2) {
                pinchZoomRef.current?.resetTransform();
                return;
            }

            if (scale >= 1 && scale < 1.5) {
                pinchZoomRef.current?.centerView(1.5);
                return;
            }

            if (scale >= 1.5 && scale < 2) {
                pinchZoomRef.current?.centerView(2);
                return;
            }

            pinchZoomRef.current?.resetTransform();
        },
        [scale, startingCoords],
    );
    // #endregion

    const doubleTapBindings = useDoubleTap(handleOnClick);

    const cursor = useMemo(
        () =>
            `url("${getAssetUrl(
                scale === 2
                    ? 'static/img/pdp/magnifying-glass-zoom-out.svg'
                    : 'static/img/pdp/magnifying-glass-zoom-in.svg',
            )}")`,
        [scale],
    );

    const hasPointerDevice = !!useBreakpointValue({
        base: false,
        md: true,
    });

    const handleOnTransformed = useCallback<NonNullable<ReactZoomPanPinchProps['onTransformed']>>(
        (ref, { scale: newScale }) => {
            // Set the scale state based on the current scale
            // This helps tap zoom & pinch zoom play along, we can go back and forth between the two
            // And by defining some scale steps (1, 1.5, 2), we avoid frequent state updates thus preventing unnecessary re-renders
            if (newScale <= 1 && scale !== 1) {
                setScale(1);
            }
            if (newScale > 1 && newScale < 2 && scale !== 1.5) {
                setScale(1.5);
            }
            if (newScale >= 2 && scale !== 2) {
                setScale(2);
            }
        },
        [scale],
    );

    return (
        <TransformWrapper
            limitToBounds={false}
            doubleClick={{
                disabled: true,
            }}
            centerOnInit
            onTransformed={handleOnTransformed}
            ref={pinchZoomRef}
        >
            <TransformComponent
                wrapperStyle={{
                    height: '100%',
                    width: '100%',
                    ...(hasPointerDevice ? { '--magnifying-glass-cursor-url': cursor } : undefined),
                }}
                wrapperClass={`flex h-full w-full items-center justify-center ${
                    hasPointerDevice ? styles.assetSectionAsset : ''
                }`}
                wrapperProps={{
                    ...(hasPointerDevice ? { onClick: handleOnClick } : { ...doubleTapBindings }),
                    onMouseDown: handleOnMouseDown,
                }}
                contentStyle={{
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <ResponsiveImage
                    id="photo-grid-zoom-asset-image"
                    className="h-full w-full object-contain"
                    src={asset.assetUrl}
                    title={asset.title}
                    alt={asset.alt}
                />
            </TransformComponent>
        </TransformWrapper>
    );
};
